/* global runtime */
// Opprettelse, import, arkivering og gjenåpning av bankutskrifter.
// Lastes som klassisk script før backend.jsx og deler dens bindingsmiljø.
const upsertBankStatement = (statement) => {
  updateState((state) => ({
    ...state,
    bankStatements: [statement, ...safeArray(state.bankStatements).filter((entry) => entry.id !== statement.id)],
  }), true);
};
const bankReconciliationMutation = async (action, payload = {}) => {
  const result = await authorizedPost('bankReconciliationMutationHttp', {
    businessId: runtime.activeBusinessId,
    action,
    payload,
  });
  if (result.state) {
    runtime.appState = mergeAppMutationState(result.state);
    emitRuntime();
  }
  return result;
};

const approveBankMatchGroup = async (group) => {
  const result = await bankReconciliationMutation('approve_match_group', { group });
  return result.group || null;
};

const bookBankMatchGroup = async (groupId) => {
  if (!groupId) throw new Error(t('bam.bank.missingMatchGroup'));
  const result = await bankReconciliationMutation('book_match_group', { groupId });
  return result.group || null;
};

const cancelBankMatchGroup = async (groupId) => {
  if (!groupId) throw new Error(t('bam.bank.missingMatchGroup'));
  const result = await bankReconciliationMutation('cancel_match_group', { groupId });
  return result.group || null;
};

const reverseBankMatchGroup = async (groupId, reason) => {
  if (!groupId) throw new Error(t('bam.bank.missingMatchGroup'));
  const result = await bankReconciliationMutation('reverse_match_group', { groupId, reason });
  return result.group || null;
};

const getBankAllocationOpenItems = (direction = 'incoming') => {
  const state = mergeState(runtime.appState);
  if (direction === 'outgoing') {
    return safeArray(state.expenses)
      .filter((expense) => expense?.isSupplierInvoice && isActiveExpense(expense))
      .map((expense) => ({ expense, summary: dashboardSupplierInvoicePaymentSummary(state, expense) }))
      .filter(({ summary }) => summary.remainingAmount > 0.005)
      .map(({ expense, summary }) => ({
        documentType: 'supplier_invoice',
        documentId: expense.id,
        number: expense.supplierInvoiceNumber || expense.voucherNumber || expense.id,
        party: expense.supplierName || 'Leverandør',
        date: expense.date || '',
        remainingAmount: backendRoundMoney(summary.remainingAmount),
      }));
  }
  return safeArray(state.invoices)
    .filter((invoice) => !['credit', 'offer', 'order_confirmation'].includes(String(invoice?.kind || '')) && isPostedInvoice(invoice, state))
    .map((invoice) => ({ invoice, summary: invoicePaymentSummary(state, invoice) }))
    .filter(({ summary }) => summary.remainingAmount > 0.005)
    .map(({ invoice, summary }) => ({
      documentType: 'invoice',
      documentId: invoice.id,
      number: invoice.invoiceNumber || invoice.id,
      party: invoice.customer?.name || invoice.customerName || 'Kunde',
      date: invoice.date || '',
      remainingAmount: backendRoundMoney(summary.remainingAmount),
    }));
};

const createBankStatementFromFileLocal = ({ fileName, source, bankAccountId, lines = [], periodFrom, periodTo, openingBalance, closingBalance, importedClosingBalance }) => {
  const now = todayIso();
  const statement = {
    id: `bank-statement-${Date.now()}`,
    source: bankUiModeToSource(source),
    bankAccountId: bankUiModeToSource(source) === 'business' ? bankAccountId : undefined,
    fileName: fileName || 'Bankutskrift',
    originalFileName: fileName || 'Bankutskrift',
    importedAt: nowIso(),
    periodFrom: periodFrom || now.slice(0, 8) + '01',
    periodTo: periodTo || now,
    status: 'active',
    openingBalance,
    closingBalance,
    importedClosingBalance: importedClosingBalance ?? closingBalance,
    lines,
  };
  upsertBankStatement(statement);
  return mapBankStatementToUi(statement);
};

// Originalutskriften er bokforingsdokumentasjonen. Ble den bare parset og kastet,
// kunne «Se original utskrift» aldri vise annet enn Plummes egen gjengivelse — og
// et avvik mellom avlest og faktisk tall var umulig aa kontrollere.
const uploadBankStatementFile = async (file, statementId) => {
  if (!file || !runtime.configured) return null;
  try {
    await initFirebase();
    if (!runtime.storage || !runtime.activeBusinessId) return null;
    // Under `businesses/<id>/` slik at Storage-reglene for foretaksfiler gjelder.
    const storagePath = `businesses/${runtime.activeBusinessId}/bankStatements/${statementId}/${file.name || 'bankutskrift'}`;
    const ref = runtime.storage.ref().child(storagePath);
    await ref.put(file, {
      contentType: file.type || 'application/octet-stream',
      contentDisposition: `inline; filename*=UTF-8''${encodeURIComponent(file.name || 'bankutskrift')}`,
      customMetadata: { originalName: file.name || 'bankutskrift' },
    });
    const downloadUrl = await ref.getDownloadURL().catch(() => '');
    return { storagePath, downloadUrl: downloadUrl || undefined, mimeType: file.type || undefined };
  } catch (error) {
    // Uten originalen mangler en visning, ikke en avstemming. Importen skal ikke
    // ryke fordi opplastingen feilet.
    console.warn('Kunne ikke lagre originalutskriften.', error);
    return null;
  }
};

const bankStatementImageDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || ''));
  reader.onerror = () => reject(reader.error || new Error(t('bam.bank.screenshotReadFailed')));
  reader.readAsDataURL(file);
});

const analyzeBankStatementImage = async (file) => {
  if (!file || !/^image\/(?:png|jpeg|webp)$/i.test(file.type || '')) {
    throw new Error(t('bam.bank.screenshotFormat'));
  }
  if (file.size > 8 * 1024 * 1024) throw new Error(t('bam.bank.screenshotTooLarge'));
  const dataUrl = await bankStatementImageDataUrl(file);
  const result = await authorizedPost('analyzeBankStatementImageHttp', {
    businessId: runtime.activeBusinessId,
    fileName: file.name || 'bankutskrift.png',
    mimeType: file.type,
    base64: dataUrl,
  }, {
    requestTimeoutMs: 90_000,
    timeoutMessage: t('bam.bank.screenshotTimeout'),
  });
  return result.parsed || { lines: [] };
};

const createBankStatementFromFile = async (input = {}) => {
  const statement = {
    source: bankUiModeToSource(input.source),
    bankAccountId: bankUiModeToSource(input.source) === 'business' ? input.bankAccountId : undefined,
    fileName: input.fileName || 'Bankutskrift',
    originalFileName: input.fileName || 'Bankutskrift',
    periodFrom: input.periodFrom,
    periodTo: input.periodTo,
    openingBalance: input.openingBalance,
    closingBalance: input.closingBalance,
    importedClosingBalance: input.importedClosingBalance ?? input.closingBalance,
    lines: safeArray(input.lines),
  };
  try {
    const result = await bankReconciliationMutation('create_statement', { statement });
    if (!result.statement) return null;
    const stored = await uploadBankStatementFile(input.file, result.statement.id);
    if (stored) {
      await bankReconciliationMutation('update_statement_file', {
        statementId: result.statement.id,
        file: stored,
      }).catch((error) => console.warn('Kunne ikke lagre referansen til originalutskriften.', error));
    }
    try {
      await prepareImportedBankStatement(result.statement.id, result.statement.source);
    } catch (analysisError) {
      console.warn('Automatisk klargjøring av bankutskriften feilet.', analysisError);
      await bankReconciliationMutation('update_balances', { statementId: result.statement.id, patch: {} }).catch(() => {});
    }
    const current = safeArray(runtime.appState?.bankStatements).find((entry) => entry.id === result.statement.id) || result.statement;
    return mapBankStatementToUi(current);
  } catch (error) {
    if (!shouldFallbackToClientStateWrite(error)) throw error;
    return createBankStatementFromFileLocal(input);
  }
};

const bankStatementCanBeArchived = (statement) => {
  const source = statement.source || 'business';
  const lines = safeArray(statement.lines);
  if (source === 'private') return true;
  if (window.PlummeBankImportBalanceStatus.missing(statement) || window.PlummeBankImportBalanceStatus.partial(statement)) return false;
  if (!lines.length && !statement.periodImport) return false;
  const lineTotal = lines.reduce((sum, line) =>
    sum + (line?.reconciliationStatus === 'import-removed' || line?.importCorrectionAction === 'removed' ? 0 : num(line.amount)), 0);
  const openingBalance = num(statement.openingBalance ?? statement.openingBalanceAmount);
  const closingBalance = num(statement.closingBalance ?? statement.closingBalanceAmount ?? (openingBalance + lineTotal));
  const balanceDeviation = closingBalance - openingBalance - lineTotal;
  if (Math.abs(balanceDeviation) >= 0.005) return false;
  return lines.every((line) => ['godkjent', 'matchet', 'privat', 'drift', 'ignorer', 'korrigert'].includes(getBankLineStatus(line, source)));
};

const assertBankLedgerControlLocal = (state, statement) => {
  if ((statement?.source || 'business') !== 'business') return;
  const end = window.PlummeBankPeriodControl.date(statement?.periodTo);
  if (!end) return;
  const control = window.PlummeBankPeriodControl.build(
    state,
    Number(end.slice(0, 4)),
    Number(end.slice(5, 7)),
    window.PlummeBankPeriodControl.trialBalanceReader(state, deriveTrialBalance),
  );
  const row = control.rows.find((entry) => window.PlummeBankPeriodControl.matches(statement, entry.account));
  if (row?.setupIssue) {
    const error = new Error(t('bam.bank.openingSetupMissing'));
    error.details = { reason: 'bank-opening-setup-required', bankAccountId: row.account?.id, setupIssue: row.setupIssue };
    throw error;
  }
  const issue = window.PlummeBankPeriodControl.statementBalanceIssue(control, statement);
  if (issue || row?.beforeStartPostings.length) {
    const error = new Error(t('bam.bank.ledgerMismatch'));
    error.details = { reason: 'bank-ledger-mismatch', ...issue };
    throw error;
  }
};

const repairMissingBankLinePostingsLocal = (state, statementId) => {
  const statement = safeArray(state.bankStatements).find((entry) => entry.id === statementId);
  if (!statement) return state;
  const existing = new Set(safeArray(state.bankReconciliationPostings).map((posting) => String(posting?.id || '')).filter(Boolean));
  const bankAccountCode = bankStatementLedgerAccountCode(state, statement, '');
  const repaired = safeArray(statement.lines)
    .filter((line) => line?.matchedEntryType === 'bank-posting')
    .filter((line) => line?.matchedEntryId && !existing.has(String(line.matchedEntryId)))
    .filter((line) => ['rounding', 'bank-fee', 'owner-settlement'].includes(String(line?.bookingType || '')))
    .map((line) => {
      const accountCode = String(line.accountCode || line.aiSuggestedAccountCode || '').trim();
      if (!accountCode || !bankAccountCode) return null;
      return {
        ...buildControlledBankLinePosting({
        bookingType: line.bookingType,
        statementId: statement.id,
        lineId: line.id,
        date: line.date || todayIso(),
        description: line.description || 'Bankpostering',
        amount: line.amount,
        accountCode,
        bankAccountCode,
        bankTrace: bankLineTrace(statement, line),
        }),
        id: line.matchedEntryId,
      };
    })
    .filter(Boolean);
  if (!repaired.length) return state;
  return {
    ...state,
    bankReconciliationPostings: [
      ...repaired,
      ...safeArray(state.bankReconciliationPostings),
    ],
  };
};

const archiveBankStatementLocal = (statementId) => {
  let archivedStatement = null;
  updateState((state) => {
    state = repairMissingBankLinePostingsLocal(state, statementId);
    const statements = safeArray(state.bankStatements);
    const statement = statements.find((entry) => entry.id === statementId);
    if (!statement) throw new Error(t('svc.stmtNotFound'));
    if (statement.bookedAt) throw new Error(t('svc.stmtAlreadyPosted'));
    assertBankLedgerControlLocal(state, statement);
    if (!bankStatementCanBeArchived(statement)) throw new Error(t('svc.stmtLinesRemain'));
    const next = {
      ...statement,
      status: 'archived',
      reconciledAt: statement.reconciledAt || nowIso(),
    };
    archivedStatement = next;
    return {
      ...state,
      bankStatements: statements.map((entry) => entry.id === statementId ? next : entry),
    };
  }, true);
  return archivedStatement ? mapBankStatementToUi(archivedStatement) : null;
};

const archiveBankStatement = async (statementId) => {
  try {
    const result = await bankReconciliationMutation('archive', { statementId });
    return result.statement ? mapBankStatementToUi(result.statement) : null;
  } catch (error) {
    if (!shouldFallbackToClientStateWrite(error)) throw error;
    return archiveBankStatementLocal(statementId);
  }
};

const previewBankStatementArchive = async (statementId) => {
  if (!statementId) throw new Error(t('bam.bank.missingStatement'));
  const result = await bankReconciliationMutation('preview_archive', { statementId });
  return result.ledgerBalanceIssue || null;
};

const reopenBankStatementLocal = (statementId) => {
  let reopenedStatement = null;
  updateState((state) => {
    const statements = safeArray(state.bankStatements);
    const statement = statements.find((entry) => entry.id === statementId);
    if (!statement) throw new Error(t('svc.stmtNotFound'));
    if (statement.bookedAt) throw new Error(t('svc.stmtMustReverse'));
    const next = {
      ...statement,
      reopenedFromStatus: statement.status,
      reopenedAt: nowIso(),
      status: 'active',
      reconciledAt: undefined,
    };
    reopenedStatement = next;
    return {
      ...state,
      bankStatements: statements.map((entry) => entry.id === statementId ? next : entry),
    };
  }, true);
  return reopenedStatement ? mapBankStatementToUi(reopenedStatement) : null;
};

const reopenBankStatement = async (statementId) => {
  try {
    const result = await bankReconciliationMutation('reopen', { statementId });
    return result.statement ? mapBankStatementToUi(result.statement) : null;
  } catch (error) {
    if (!shouldFallbackToClientStateWrite(error)) throw error;
    return reopenBankStatementLocal(statementId);
  }
};
